import { useQuery as useReactQuery } from "@tanstack/react-query";
import { useAction } from "convex/react";
import {
	FileArchiveIcon,
	FileAudioIcon,
	FileCodeIcon,
	FileIcon,
	FileImageIcon,
	FileJsonIcon,
	FileSpreadsheetIcon,
	FileTextIcon,
	FileVideoIcon,
	type LucideIcon,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { MailMessageDetail } from "./types";

type Attachment = MailMessageDetail["attachments"][number];

const INITIAL_VISIBLE = 2;

function iconForContentType(contentType: string): LucideIcon {
	const ct = contentType.toLowerCase();
	if (ct.startsWith("image/")) return FileImageIcon;
	if (ct.startsWith("video/")) return FileVideoIcon;
	if (ct.startsWith("audio/")) return FileAudioIcon;
	if (ct === "application/pdf") return FileTextIcon;
	if (ct.includes("zip") || ct.includes("compressed") || ct.includes("tar"))
		return FileArchiveIcon;
	if (ct.includes("spreadsheet") || ct.includes("excel") || ct.includes("csv"))
		return FileSpreadsheetIcon;
	if (ct.includes("json")) return FileJsonIcon;
	if (
		ct.includes("javascript") ||
		ct.includes("typescript") ||
		ct.includes("xml") ||
		ct.includes("html")
	)
		return FileCodeIcon;
	if (ct.includes("word") || ct.includes("document") || ct.startsWith("text/"))
		return FileTextIcon;
	return FileIcon;
}

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(contentType: string): boolean {
	return contentType.toLowerCase().startsWith("image/");
}

interface AttachmentChipProps {
	attachment: Attachment;
	downloading: boolean;
	onDownload: () => void;
}

function AttachmentThumbnail({
	attachmentId,
	alt,
}: {
	attachmentId: Id<"mailAttachments">;
	alt: string;
}) {
	const getUrl = useAction(api.mail.attachments.getAttachmentDownloadUrl);
	const { data, isError } = useReactQuery({
		queryKey: ["mail-attachment-url", attachmentId],
		queryFn: () => getUrl({ attachmentId }),
		staleTime: Number.POSITIVE_INFINITY,
		retry: false,
	});
	const [loadFailed, setLoadFailed] = useState(false);
	const failed = isError || loadFailed;

	if (failed || !data) {
		return (
			<div
				className={cn(
					"size-5 shrink-0 rounded border border-border bg-muted",
					failed && "opacity-50",
				)}
				aria-hidden
			/>
		);
	}
	return (
		<img
			src={data.url}
			alt={alt}
			loading="lazy"
			referrerPolicy="no-referrer"
			className="size-5 shrink-0 rounded border border-border object-cover"
			onError={() => setLoadFailed(true)}
		/>
	);
}

function AttachmentChip({
	attachment,
	downloading,
	onDownload,
}: AttachmentChipProps) {
	const Icon = iconForContentType(attachment.contentType);
	const showThumbnail = isImage(attachment.contentType);
	return (
		<Button
			variant="outline"
			size="sm"
			disabled={downloading}
			onClick={onDownload}
			className="flex h-8 items-center gap-2"
		>
			{showThumbnail ? (
				<AttachmentThumbnail
					attachmentId={attachment._id}
					alt={attachment.name}
				/>
			) : (
				<Icon data-icon="inline-start" />
			)}
			<span className="max-w-[220px] truncate">{attachment.name}</span>
			<span className="text-muted-foreground">
				{formatBytes(attachment.size)}
			</span>
		</Button>
	);
}

interface MailAttachmentsProps {
	attachments: Attachment[];
	downloadingId: Id<"mailAttachments"> | null;
	onDownload: (attachmentId: Id<"mailAttachments">, name: string) => void;
}

export function MailAttachments({
	attachments,
	downloadingId,
	onDownload,
}: MailAttachmentsProps) {
	const [expanded, setExpanded] = useState(false);
	if (attachments.length === 0) return null;

	const visible = expanded
		? attachments
		: attachments.slice(0, INITIAL_VISIBLE);
	const hiddenCount = attachments.length - visible.length;

	return (
		<div className="flex flex-wrap items-center gap-2 p-4">
			{visible.map((a) => (
				<AttachmentChip
					key={a._id}
					attachment={a}
					downloading={downloadingId === a._id}
					onDownload={() => onDownload(a._id, a.name)}
				/>
			))}
			{!expanded && hiddenCount > 0 && (
				<Button
					variant="ghost"
					size="sm"
					onClick={() => setExpanded(true)}
					className="h-8"
				>
					+{hiddenCount} more
				</Button>
			)}
			{expanded && attachments.length > INITIAL_VISIBLE && (
				<Button
					variant="ghost"
					size="sm"
					onClick={() => setExpanded(false)}
					className="h-8"
				>
					Show less
				</Button>
			)}
		</div>
	);
}
